<template>
  <div class="feishu-config-page">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>管理员飞书身份授权</span>
        </div>
      </template>
      <el-alert type="error" :closable="false" show-icon class="mb-12">
        <div style="font-weight:600;margin-bottom:6px">管理员回调地址（飞书白名单必须完全一致）：</div>
        <div
          style="background:#eaf4ff;color:#1d4ed8;padding:8px 12px;border-radius:6px;word-break:break-all;display:flex;align-items:center;justify-content:space-between;gap:12px"
        >
          <span style="font-family:monospace;font-size:13px">{{ adminCallbackUrl }}</span>
          <el-button size="small" type="primary" @click="copyText(adminCallbackUrl)">复制到剪贴板</el-button>
        </div>
        <div v-if="!form.feishu_admin_redirect_uri" style="margin-top:8px;color:#ca8a04">
          ⚠️ 当前未显式配置管理员回调地址，正在使用系统自动拼接值（反代环境可能不准确）。请在下方「飞书群管理配置」表单中填写管理员回调地址后保存。
        </div>
      </el-alert>

      <el-descriptions :column="2" border size="default">
        <el-descriptions-item label="授权状态">
          <el-tag v-if="!adminTokenStatus.authorized" type="info">未授权</el-tag>
          <el-tag v-else-if="adminTokenStatus.expired" type="danger">已过期</el-tag>
          <el-tag v-else-if="adminTokenStatus.will_expire_soon" type="warning">即将过期</el-tag>
          <el-tag v-else type="success">有效</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="管理员名称">
          <span v-if="adminTokenStatus.name">{{ adminTokenStatus.name }}</span>
          <span v-else style="color:#909399">-</span>
        </el-descriptions-item>
        <el-descriptions-item label="管理员 OpenID">
          <span style="font-family:monospace;font-size:12px" v-if="adminTokenStatus.open_id">{{ adminTokenStatus.open_id }}</span>
          <span v-else style="color:#909399">-</span>
        </el-descriptions-item>
        <el-descriptions-item label="过期时间">
          <span v-if="adminTokenStatus.expire_at">{{ formatDateTime(adminTokenStatus.expire_at) }}</span>
          <span v-else style="color:#909399">-</span>
        </el-descriptions-item>
        <el-descriptions-item label="续期令牌" :span="2">
          <el-tag v-if="adminTokenStatus.has_refresh_token" type="success" size="small">有 refresh_token（过期自动续期）</el-tag>
          <el-tag v-else type="info" size="small">无（需重新授权）</el-tag>
        </el-descriptions-item>
      </el-descriptions>

      <div style="margin-top:16px;display:flex;gap:10px;flex-wrap:wrap">
        <el-button type="primary" :loading="authorizing" @click="startAdminAuth">
          {{ adminTokenStatus.authorized && !adminTokenStatus.expired ? '重新授权' : '去授权' }}
        </el-button>
        <el-button :loading="refreshingStatus" @click="refreshAdminStatus">刷新状态</el-button>
        <el-button type="danger" :loading="clearingToken" v-if="adminTokenStatus.authorized" @click="clearAdminToken">清除授权</el-button>
      </div>
    </el-card>

    <el-card style="margin-top:16px">
      <template #header>
        <div class="card-header">
          <span>飞书群管理配置</span>
        </div>
      </template>
      <el-form :model="form" label-width="160px" label-position="right">
        <el-form-item label="App ID">
          <el-input v-model="form.feishu_app_id" placeholder="请输入飞书自建应用 App ID" />
        </el-form-item>
        <el-form-item label="App Secret">
          <el-input v-model="form.feishu_app_secret" type="password" show-password placeholder="请输入飞书自建应用 App Secret" />
        </el-form-item>
        <el-form-item label="OAuth 回调地址">
          <el-input v-model="form.feishu_redirect_uri" placeholder="https://your-domain.com/v1/feishu/callback" />
          <div style="width:100%;color:#909399;font-size:12px;margin-top:4px">用于 H5 端飞书绑定流程的回调地址（非管理员授权）</div>
        </el-form-item>
        <el-form-item label="管理员回调地址">
          <el-input v-model="form.feishu_admin_redirect_uri" placeholder="https://stock-api.aibot6.cn/v1/feishu/admin-callback" />
          <div style="width:100%;color:#909399;font-size:12px;margin-top:4px">⚠️ 必须与上方卡片「管理员回调地址」完全一致，且在飞书开放平台「安全设置 → 重定向 URL 白名单」中加入该地址。强烈建议显式填写，避免反代环境自动拼接出错。</div>
        </el-form-item>
        <el-form-item label="前端域名">
          <el-input v-model="form.feishu_frontend_domain" placeholder="https://stock.aibot6.cn" />
        </el-form-item>
        <el-form-item label="事件回调地址">
          <el-input v-model="form.feishu_event_callback_url" placeholder="https://stock-api.aibot6.cn/v1/feishu/event" />
        </el-form-item>
        <el-form-item label="Verification Token">
          <el-input v-model="form.feishu_verification_token" placeholder="请输入 Verification Token" />
        </el-form-item>
        <el-form-item label="Encrypt Key">
          <el-input v-model="form.feishu_encrypt_key" type="password" show-password placeholder="请输入 Encrypt Key（可为空）" />
        </el-form-item>
        <el-form-item label="群分享链接">
          <div style="width:100%;display:flex;gap:8px;flex-wrap:wrap">
            <el-input v-model="form.feishu_group_share_url" placeholder="点击右侧按钮自动生成并保存群分享链接" style="flex:1;min-width:320px" />
            <el-button type="success" :loading="generatingShareLink" @click="generateGroupShareUrl">
              {{ form.feishu_group_share_url ? '重新生成群分享链接' : '获取群分享链接' }}
            </el-button>
            <el-button v-if="form.feishu_group_share_url" @click="copyText(form.feishu_group_share_url)">复制链接</el-button>
          </div>
          <div
            v-if="form.feishu_group_share_url"
            class="share-expire-line"
          >
            链接过期时间：
            <strong v-if="form.feishu_group_share_is_permanent">永久</strong>
            <strong v-else-if="form.feishu_group_share_expire_time">{{ formatExpireTime(form.feishu_group_share_expire_time) }}</strong>
            <strong v-else>—</strong>
          </div>
        </el-form-item>
        <el-form-item label="群聊ID (chat_id)">
          <el-input v-model="form.feishu_chat_id" placeholder="例如：oc_xxxxxxxxxxxxxx" />
        </el-form-item>
        <el-form-item label="最低加群等级">
          <el-input-number v-model="form.feishu_min_vip_level" :min="1" :max="10" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="saveSettings">保存配置</el-button>
          <el-button type="warning" @click="checkExpired">检查过期会员踢群</el-button>
          <el-button type="success" @click="loadGroupMembers">飞书群管理</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card v-if="expiredResult" style="margin-top:16px">
      <template #header>
        <div class="card-header">
          <span>过期踢群结果</span>
        </div>
      </template>
      <el-descriptions :column="3" border size="default">
        <el-descriptions-item label="检查数量">{{ expiredResult.checked }}</el-descriptions-item>
        <el-descriptions-item label="踢出数量">{{ expiredResult.kicked?.length ?? 0 }}</el-descriptions-item>
        <el-descriptions-item label="错误数量">{{ expiredResult.errors?.length ?? 0 }}</el-descriptions-item>
      </el-descriptions>
      <el-divider v-if="expiredResult.kicked?.length">踢出明细</el-divider>
      <el-table v-if="expiredResult.kicked?.length" :data="expiredResult.kicked" size="small" border stripe>
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="username" label="账号" />
        <el-table-column prop="open_id" label="飞书OpenID" />
      </el-table>
      <el-divider v-if="expiredResult.errors?.length">错误明细</el-divider>
      <el-table v-if="expiredResult.errors?.length" :data="expiredResult.errors" size="small" border stripe>
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="username" label="账号" />
        <el-table-column prop="error" label="错误信息" show-overflow-tooltip />
      </el-table>
    </el-card>

    <el-card style="margin-top:16px" v-if="groupMembers.length > 0 || loadingMembers">
      <template #header>
        <div class="card-header">
          <span>飞书群成员列表（{{ groupMembers.length }}）</span>
          <el-button size="small" type="primary" :loading="loadingMembers" @click="loadGroupMembers">刷新</el-button>
        </div>
      </template>
      <el-table v-loading="loadingMembers" :data="groupMembers" border stripe size="default">
        <el-table-column prop="feishu_nickname" label="飞书昵称" min-width="140" show-overflow-tooltip />
        <el-table-column prop="feishu_open_id" label="飞书OpenID" min-width="200" show-overflow-tooltip />
        <el-table-column prop="website_id" label="站ID" width="90" />
        <el-table-column prop="username" label="站内账号" width="140" show-overflow-tooltip />
        <el-table-column label="会员等级" width="110">
          <template #default="{ row }">
            <el-tag v-if="row.vip_level != null" type="warning" size="small">Lv{{ row.vip_level }}</el-tag>
            <span v-else style="color:#909399">未绑定</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="140" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" link v-if="!row.website_id" @click="openBindDialog(row)">绑定</el-button>
            <el-button type="danger" size="small" link @click="kickMember(row)">踢出</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog v-model="bindDialogVisible" title="绑定飞书ID到站内账号" width="520px">
      <el-form label-width="120px">
        <el-form-item label="飞书OpenID">
          <el-input :model-value="bindTarget?.feishu_open_id" disabled />
        </el-form-item>
        <el-form-item label="飞书昵称">
          <el-input :model-value="bindTarget?.feishu_nickname" disabled />
        </el-form-item>
        <el-form-item label="站内用户ID">
          <el-input v-model="bindUserId" placeholder="请输入要绑定的站内用户ID（数字）" type="number" />
          <div style="width:100%;color:#909399;font-size:12px;margin-top:4px">
            绑定后该飞书用户将与站内ID对应，等级不足时将按策略被踢出群聊。
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="bindDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="bindLoading" @click="confirmBind">确认绑定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import request from '@/utils/request'

const form = reactive({
  feishu_app_id: '',
  feishu_app_secret: '',
  feishu_redirect_uri: '',
  feishu_admin_redirect_uri: '',
  feishu_frontend_domain: '',
  feishu_event_callback_url: '',
  feishu_verification_token: '',
  feishu_encrypt_key: '',
  feishu_group_share_url: '',
  feishu_group_share_expire_time: '',
  feishu_group_share_is_permanent: false,
  feishu_chat_id: '',
  feishu_min_vip_level: 2,
})

const generatingShareLink = ref(false)

const adminCallbackUrl = ref('')
const adminTokenStatus = reactive({
  authorized: false,
  valid: false,
  name: '',
  open_id: '',
  expire_at: 0,
  expired: false,
  will_expire_soon: false,
  has_refresh_token: false,
})

const authorizing = ref(false)
const refreshingStatus = ref(false)
const clearingToken = ref(false)

const expiredResult = ref(null)
const groupMembers = ref([])
const loadingMembers = ref(false)
const bindDialogVisible = ref(false)
const bindTarget = ref(null)
const bindUserId = ref('')
const bindLoading = ref(false)

const formatDateTime = (ts) => {
  if (!ts || Number(ts) <= 0) return '-'
  const d = new Date(Number(ts) * 1000)
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

const formatExpireTime = (value) => {
  const raw = String(value || '').trim()
  if (!raw) return '—'
  const ts = Number(raw)
  if (Number.isFinite(ts) && ts > 0) {
    // 兼容秒 / 毫秒时间戳
    const sec = ts > 1e12 ? Math.floor(ts / 1000) : ts
    return formatDateTime(sec)
  }
  return raw
}

function parsePermanentFlag(v) {
  if (v === true || v === 1 || v === '1') return true
  if (v === false || v === 0 || v === '0' || v == null || v === '') return false
  return String(v).toLowerCase() === 'true' || String(v).toLowerCase() === 'yes'
}

function copyText(text) {
  if (!text) return
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(String(text)).then(() => {
        ElMessage.success('已复制到剪贴板')
      }).catch(() => {
        fallbackCopy(text)
      })
    } else {
      fallbackCopy(text)
    }
  } catch (e) {
    fallbackCopy(text)
  }
}
function fallbackCopy(text) {
  try {
    const textarea = document.createElement('textarea')
    textarea.value = String(text)
    textarea.style.position = 'fixed'
    textarea.style.left = '-9999px'
    document.body.appendChild(textarea)
    textarea.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(textarea)
    if (ok) ElMessage.success('已复制到剪贴板')
    else ElMessage.error('复制失败，请手动复制')
  } catch (e) {
    ElMessage.error('复制失败，请手动复制')
  }
}

async function loadSettings() {
  try {
    const data = await request.get('/v1/feishu/settings')
    form.feishu_app_id = data.feishu_app_id || ''
    form.feishu_app_secret = data.feishu_app_secret || ''
    form.feishu_redirect_uri = data.feishu_redirect_uri || ''
    form.feishu_admin_redirect_uri = data.feishu_admin_redirect_uri || ''
    form.feishu_frontend_domain = data.feishu_frontend_domain || ''
    form.feishu_event_callback_url = data.feishu_event_callback_url || ''
    form.feishu_verification_token = data.feishu_verification_token || ''
    form.feishu_encrypt_key = data.feishu_encrypt_key || ''
    form.feishu_group_share_url = data.feishu_group_share_url || ''
    form.feishu_group_share_expire_time = String(data.feishu_group_share_expire_time || '').trim()
    form.feishu_group_share_is_permanent = parsePermanentFlag(data.feishu_group_share_is_permanent)
    form.feishu_chat_id = data.feishu_chat_id || ''
    form.feishu_min_vip_level = Number(data.feishu_min_vip_level || 2)
    adminCallbackUrl.value = data.admin_callback_url || ''
    if (data.admin_token_status && typeof data.admin_token_status === 'object') {
      Object.assign(adminTokenStatus, data.admin_token_status)
    }
  } catch (e) {
    ElMessage.error(typeof e === 'string' ? e : (e?.message || '获取飞书配置失败'))
  }
}

async function saveSettings() {
  const payload = {
    feishu_app_id: form.feishu_app_id,
    feishu_app_secret: form.feishu_app_secret,
    feishu_redirect_uri: form.feishu_redirect_uri,
    feishu_admin_redirect_uri: form.feishu_admin_redirect_uri,
    feishu_frontend_domain: form.feishu_frontend_domain,
    feishu_event_callback_url: form.feishu_event_callback_url,
    feishu_verification_token: form.feishu_verification_token,
    feishu_encrypt_key: form.feishu_encrypt_key,
    feishu_group_share_url: form.feishu_group_share_url,
    feishu_chat_id: form.feishu_chat_id,
    feishu_min_vip_level: Number(form.feishu_min_vip_level || 0),
  }
  try {
    await request.post('/v1/feishu/update-settings', payload)
    ElMessage.success('保存成功')
    await loadSettings()
    await loadAdminTokenStatus()
  } catch (e) {
    ElMessage.error(typeof e === 'string' ? e : (e?.message || '保存配置失败'))
  }
}

async function generateGroupShareUrl() {
  if (!form.feishu_chat_id) {
    ElMessage.warning('请先填写并保存群聊ID (chat_id)')
    return
  }
  generatingShareLink.value = true
  try {
    const data = await request.post('/v1/feishu/generate-group-share-link')
    const shareLink = String(data?.share_link || '').trim()
    if (shareLink) {
      form.feishu_group_share_url = shareLink
    }
    // 直接用接口返回的群分享链接过期信息（不要被错误的 is_permanent 覆盖）
    form.feishu_group_share_expire_time = String(data?.expire_time ?? '').trim()
    form.feishu_group_share_is_permanent = parsePermanentFlag(data?.is_permanent)
    ElMessage.success('群分享链接生成并保存成功')
    const expireSnap = form.feishu_group_share_expire_time
    const permanentSnap = form.feishu_group_share_is_permanent
    const urlSnap = form.feishu_group_share_url
    await loadSettings()
    // 接口返回优先：保证群分享链接过期时间立刻显示在输入框下方
    if (urlSnap) form.feishu_group_share_url = urlSnap
    if (expireSnap) form.feishu_group_share_expire_time = expireSnap
    form.feishu_group_share_is_permanent = permanentSnap
  } catch (e) {
    ElMessage.error(typeof e === 'string' ? e : (e?.message || '获取群分享链接失败'))
  } finally {
    generatingShareLink.value = false
  }
}

async function loadAdminTokenStatus() {
  try {
    const data = await request.get('/v1/feishu/admin-token-status')
    if (data && typeof data === 'object') {
      Object.assign(adminTokenStatus, data)
    }
  } catch (e) {
    // ignore
  }
}
function refreshAdminStatus() {
  refreshingStatus.value = true
  loadAdminTokenStatus().finally(() => { refreshingStatus.value = false })
}

async function startAdminAuth() {
  authorizing.value = true
  try {
    const data = await request.get('/v1/feishu/admin-auth-url')
    const url = (data && data.url) ? data.url : ''
    if (!url) {
      ElMessage.error('后端未返回授权地址')
      return
    }
    window.location.href = url
  } catch (e) {
    ElMessage.error(typeof e === 'string' ? e : (e?.message || '获取授权地址失败'))
  } finally {
    authorizing.value = false
  }
}

async function clearAdminToken() {
  if (!adminTokenStatus.authorized) return
  try {
    await ElMessageBox.confirm('确认清除当前管理员飞书授权？清除后踢群等敏感操作需重新授权。', '清除授权确认', {
      type: 'warning',
      confirmButtonText: '确认清除',
      cancelButtonText: '取消',
    })
  } catch (e) {
    return
  }
  clearingToken.value = true
  try {
    await request.post('/v1/feishu/admin-token-clear')
    ElMessage.success('已清除管理员授权')
    await loadAdminTokenStatus()
  } catch (e) {
    ElMessage.error(typeof e === 'string' ? e : (e?.message || '清除授权失败'))
  } finally {
    clearingToken.value = false
  }
}

async function ensureAdminTokenValid() {
  if (adminTokenStatus.authorized && !adminTokenStatus.expired && adminTokenStatus.valid) {
    return true
  }
  try {
    await loadAdminTokenStatus()
  } catch (e) {}
  if (adminTokenStatus.authorized && !adminTokenStatus.expired && adminTokenStatus.valid) {
    return true
  }
  try {
    await ElMessageBox.confirm(
      '当前管理员飞书授权未完成或已过期。踢出群成员、过期踢群等敏感操作必须使用管理员个人 user_access_token，是否先去完成授权？',
      '请先完成管理员授权',
      {
        type: 'warning',
        confirmButtonText: '去授权',
        cancelButtonText: '取消',
      }
    )
  } catch (e) {
    return false
  }
  await startAdminAuth()
  return false
}

async function checkExpired() {
  const ok = await ensureAdminTokenValid()
  if (!ok) return
  try {
    await ElMessageBox.confirm('确认执行过期踢群？将扫描所有会员等级不足且绑定了飞书ID的用户，并将其从群聊中移除。', '操作确认', {
      type: 'warning',
      confirmButtonText: '继续执行',
      cancelButtonText: '取消',
    })
  } catch (e) {
    return
  }
  try {
    const data = await request.post('/v1/feishu/check-expired')
    expiredResult.value = data || null
    ElMessage.success('执行完成')
  } catch (e) {
    ElMessage.error(typeof e === 'string' ? e : (e?.message || '检查过期会员失败'))
  }
}

async function loadGroupMembers() {
  loadingMembers.value = true
  try {
    const data = await request.get('/v1/feishu/group-members')
    groupMembers.value = (data && data.members) || []
    ElMessage.success(`已加载 ${groupMembers.value.length} 位群成员`)
  } catch (e) {
    ElMessage.error(typeof e === 'string' ? e : (e?.message || '加载群成员失败'))
  } finally {
    loadingMembers.value = false
  }
}

function openBindDialog(row) {
  bindTarget.value = row
  bindUserId.value = ''
  bindDialogVisible.value = true
}

async function confirmBind() {
  if (!bindTarget.value || !bindTarget.value.feishu_open_id) {
    ElMessage.warning('缺少飞书 OpenID')
    return
  }
  const uid = Number(bindUserId.value)
  if (!uid || uid <= 0) {
    ElMessage.warning('请输入有效的站内用户ID（正整数）')
    return
  }
  bindLoading.value = true
  try {
    await request.post('/v1/feishu/manual-bind', {
      user_id: uid,
      open_id: bindTarget.value.feishu_open_id,
    })
    ElMessage.success('绑定成功')
    bindDialogVisible.value = false
    bindTarget.value = null
    await loadGroupMembers()
  } catch (e) {
    ElMessage.error(typeof e === 'string' ? e : (e?.message || '绑定失败'))
  } finally {
    bindLoading.value = false
  }
}

async function kickMember(row) {
  if (!row || !row.feishu_open_id) {
    ElMessage.warning('缺少飞书 OpenID')
    return
  }
  const ok = await ensureAdminTokenValid()
  if (!ok) return
  try {
    await ElMessageBox.confirm(
      `确定将【${row.feishu_nickname || row.feishu_open_id}】踢出飞书群？`,
      '踢出确认',
      {
        type: 'warning',
        confirmButtonText: '踢出',
        cancelButtonText: '取消',
      }
    )
  } catch (e) {
    return
  }
  try {
    await request.post('/v1/feishu/kick-member', { open_id: row.feishu_open_id })
    ElMessage.success('踢出成功')
    await loadGroupMembers()
  } catch (e) {
    ElMessage.error(typeof e === 'string' ? e : (e?.message || '踢出失败'))
  }
}

onMounted(async () => {
  await loadSettings()
  await loadAdminTokenStatus()

  try {
    if (window.location && window.location.hash) {
      const hash = window.location.hash
      const idx = hash.indexOf('?')
      if (idx !== -1) {
        const query = hash.substring(idx + 1)
        const params = new URLSearchParams(query)
        const adminAuth = params.get('admin_auth')
        const msg = params.get('msg')
        if (adminAuth === 'success') {
          ElMessage.success('管理员飞书授权成功！')
        } else if (adminAuth === 'error') {
          ElMessage.error(msg || '管理员飞书授权失败，请重试')
        }
        if (adminAuth) {
          const baseHash = hash.substring(0, idx)
          const cleanUrl = window.location.pathname + window.location.search + baseHash
          window.history.replaceState(null, document.title, cleanUrl)
          await loadAdminTokenStatus()
        }
      }
    }
  } catch (e) {}
})
</script>

<style scoped>
.feishu-config-page {
  padding: 4px 2px 24px;
}
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
}
.mb-12 {
  margin-bottom: 12px;
}
.share-expire-line {
  width: 100%;
  margin-top: 8px;
  color: #606266;
  font-size: 12px;
  line-height: 1.6;
}
.share-expire-line strong {
  color: #303133;
  font-weight: 600;
}
</style>
